import { useState } from "react";
import { Bell, X, Check, Heart, Zap, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface SimpleNotification {
  id: number;
  title: string;
  message: string;
  type: 'journey_milestone' | 'component_unlock' | 'encouragement' | 'reminder' | 'system';
  category: 'progress' | 'achievement' | 'tip' | 'warning' | 'celebration';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  emotionalTone: 'encouraging' | 'celebratory' | 'gentle' | 'urgent';
  journeyStep?: number;
  componentType?: 'life' | 'money' | 'joy' | 'goodwill';
  isRead: boolean;
  createdAt: string;
}

export default function SimpleNotificationCenter() {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<SimpleNotification[]>([
    {
      id: 1,
      title: "Welcome to Your Journey!",
      message: "You've started your Red Helicopter transformation. Let's build your leadership helicopter together.",
      type: 'journey_milestone',
      category: 'celebration',
      priority: 'high',
      emotionalTone: 'celebratory',
      journeyStep: 1,
      isRead: false,
      createdAt: new Date().toISOString()
    },
    {
      id: 2,
      title: "LIFE Component Ready",
      message: "Your personal stories unlock the helicopter body - the foundation of your leadership capacity.",
      type: 'component_unlock',
      category: 'progress',
      priority: 'medium',
      emotionalTone: 'encouraging',
      componentType: 'life',
      isRead: true,
      createdAt: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: 3,
      title: "Daily Leadership Tip",
      message: "Leadership is about agency - your power to create positive change through authentic action.",
      type: 'reminder',
      category: 'tip',
      priority: 'low',
      emotionalTone: 'gentle',
      isRead: false,
      createdAt: new Date(Date.now() - 3600000).toISOString()
    }
  ]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const markAsRead = (notificationId: number) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const getNotificationIcon = (notification: SimpleNotification) => {
    switch (notification.type) {
      case 'journey_milestone':
        return <Zap className="w-4 h-4 text-yellow-500" />;
      case 'component_unlock':
        return <Heart className="w-4 h-4 text-red-500" />;
      case 'encouragement':
        return <Heart className="w-4 h-4 text-pink-500" />;
      case 'reminder':
        return <BookOpen className="w-4 h-4 text-blue-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-50 border-red-200 hover:bg-red-100';
      case 'high':
        return 'bg-orange-50 border-orange-200 hover:bg-orange-100';
      case 'medium':
        return 'bg-blue-50 border-blue-200 hover:bg-blue-100';
      default:
        return 'bg-gray-50 border-gray-200 hover:bg-gray-100';
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="relative p-2 opacity-50 cursor-not-allowed transition-colors"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          disabled
        >
          <Bell className="w-5 h-5 text-gray-400" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 px-1 py-0 text-xs min-w-[18px] h-[18px] flex items-center justify-center rounded-full opacity-50"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-80 p-0 shadow-lg border-gray-200" 
        align="end"
        sideOffset={8}
      >
        <div className="bg-white rounded-lg overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">
                Notifications
              </h3>
              <div className="flex items-center space-x-2">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-xs px-2 py-1 h-auto text-blue-600 hover:text-blue-800"
                  >
                    Mark all read
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="p-1 h-auto hover:bg-gray-200 rounded"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="space-y-0">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                      !notification.isRead 
                        ? getPriorityColor(notification.priority)
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => markAsRead(notification.id)}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className={`text-sm font-medium truncate ${
                            !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </h4>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2" />
                          )}
                        </div>
                        
                        <p className={`text-xs leading-relaxed ${
                          !notification.isRead ? 'text-gray-700' : 'text-gray-500'
                        }`}>
                          {notification.message}
                        </p>
                        
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-gray-400">
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                          
                          {notification.journeyStep && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              Step {notification.journeyStep}
                            </Badge>
                          )}
                          
                          {notification.componentType && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs px-1.5 py-0 ${
                                notification.componentType === 'life' ? 'border-blue-300 text-blue-700' :
                                notification.componentType === 'money' ? 'border-green-300 text-green-700' :
                                notification.componentType === 'joy' ? 'border-purple-300 text-purple-700' :
                                'border-orange-300 text-orange-700'
                              }`}
                            >
                              {notification.componentType.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500 text-center">
              Stay informed about your helicopter journey progress
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}